import { Link } from "react-router-dom";
import { Calendar, ConfigProvider, Spin, Select, Typography, Input } from "antd";
import es_ES from "antd/es/locale/es_ES";
import { ScheduleOutlined, CalendarOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import axios from "axios";
import "./CalendarView.css";
import VerificationModal from "./VerificationModal";
import DayEventsModal from "./DayEventsModal";
import moment from "moment";
import { formatControlInterno } from "./calendarUtils";
import CalendarCell from "./CalendarCell"; // Import CalendarCell
import EventItem from "./EventItem"; // Import EventItem (used by CalendarCell)

const { Title, Text } = Typography;

const CalendarView = () => {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedValue, setSelectedValue] = useState(moment());
  const [filters, setFilters] = useState(["visita"]);
  const [controlInternoFilter, setControlInternoFilter] = useState("");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [selectedVisitas, setSelectedVisitas] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);

  const [isDayModalVisible, setIsDayModalVisible] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);

  const onSelect = (value) => {
    if (value.format("YYYY-MM") === selectedValue.format("YYYY-MM")) {
      setSelectedDay(value);
      setIsDayModalVisible(true);
    }
  };

  const onPanelChange = (value) => {
    setSelectedValue(value);
    setSelectedDay(null);
    setIsDayModalVisible(false);
  };

  useEffect(() => {
    const promises = [];
    if (filters.includes("visita")) {
      promises.push(
        axios.get(
          `${process.env.REACT_APP_API_URL}/verificaciones/calendario/visitas/
        ${moment(selectedValue).format("YYYY")}/
        ${moment(selectedValue).format("MM")}`
        )
      );
    }

    Promise.all(promises)
      .then((responses) => {
        let dates = [];
        responses.forEach((response) => {
          const newDates = response.data.map((date) => ({
            id: date.id,
            ControlInterno: date.ControlInterno,
            Municipio: date.Municipio,
            Fecha: date.Fecha.substring(0, 10),
            Hora1: date.Hora1,
            Hora2: date.Hora2,
            Tipo: date.Tipo,
            Estado: date.Estado,
          }));
          dates = dates.concat(newDates);
        });

        dates.sort((a, b) => {
          if (a.Hora1 && b.Hora1) {
            return a.Hora1.localeCompare(b.Hora1);
          }
          return 0;
        });

        setDates(dates);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [selectedValue, filters]);

  useEffect(() => {
    if (selectedItemId) {
      setLoadingModal(true);
      axios
        .get(`${process.env.REACT_APP_API_URL}/verificaciones/${selectedItemId}`)
        .then((response) => {
          setSelectedVerification(response.data[0]);
          if (selectedItemType === "visita") {
            axios.get(`${process.env.REACT_APP_API_URL}/visitas/${selectedItemId}`).then((visitaResponse) => {
              setSelectedVisitas(visitaResponse.data);
              setLoadingModal(false);
            });
          } else {
            setLoadingModal(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching verification data:", error);
          setLoadingModal(false);
        });
    }
  }, [selectedItemId, selectedItemType]);

  const handleItemClick = (item) => {
    setSelectedItemId(item.id);
    setSelectedItemType(item.Tipo);
    setIsModalVisible(true);
  };

  const dateCellRender = (value) => {
    const listData = dates?.filter(
      (date) =>
        moment(date.Fecha).format("YYYY-MM-DD") === value.format("YYYY-MM-DD") &&
        filters.includes(date.Tipo) &&
        (!controlInternoFilter || (date.ControlInterno && date.ControlInterno.toLowerCase().includes(controlInternoFilter.toLowerCase())))
    );

    return (
      <CalendarCell
        value={value}
        dates={listData} // Pass filtered dates to CalendarCell
        visibleTypes={filters} // Assuming filters are the visible types
        handleItemClick={handleItemClick}
      />
    );
  };

  const generalFields = [
    { key: "FechaContrato", label: "Fecha de Contrato" },
    { key: "FechaRecepcion", label: "Fecha de Solicitud" },
    { key: "DescripcionBreve", label: "Descripción Breve" },
    { key: "Monto", label: "Monto" },
  ];
  const clienteFields = [
    { key: "NombreCompletoPV", label: "Razón Social" },
    { key: "Cliente", label: "Cliente" },
  ];
  const instalacionFields = [
    { key: "TensionElectricaSuministro", label: "Tensión Eléctrica de Suministro", suffix: "V" },
    { key: "CargaInstalada", label: "Carga Instalada", suffix: "kW" },
    { key: "AlcanceVerificacion", label: "Alcance de la Verificación", suffix: "kW" },
    { key: "Capacidad", label: "Capacidad de la Subestación", suffix: "kVA" },
  ];
  const visitaFields = [
    { key: "FechaVisita", label: "Fecha de Visita" },
    { key: "HoraInicio", label: "Hora de Inicio" },
    { key: "HoraTermino", label: "Hora de Término" },
  ];

  const getDayEvents = () => {
    if (!selectedDay || !dates) return [];
    return dates.filter(
      (date) =>
        moment(date.Fecha).format("YYYY-MM-DD") === selectedDay.format("YYYY-MM-DD") &&
        filters.includes(date.Tipo) &&
        (!controlInternoFilter || (date.ControlInterno && date.ControlInterno.toLowerCase().includes(controlInternoFilter.toLowerCase())))
    );
  };

  return (
    <ConfigProvider locale={es_ES}>
      <div
        style={{
          padding: "0",
          minHeight: "100vh",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Title level={3} style={{ margin: 0, color: "#1890ff" }}>
              <CalendarOutlined style={{ marginRight: "12px" }} />
              Calendario de Verificaciones
            </Title>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <Text strong style={{ color: "#595959" }}>
                Filtrar por Control Interno:
              </Text>
              <Input
                value={controlInternoFilter}
                onChange={(e) => setControlInternoFilter(e.target.value)}
                style={{ width: 250 }}
                placeholder="Filtrar Control Interno"
              />
            </div>
          </div>
        </div>

        <Spin spinning={loading}>
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <Calendar
              value={selectedValue}
              fullscreen={true}
              dateCellRender={dateCellRender}
              onSelect={onSelect}
              onPanelChange={onPanelChange}
              locale={{
                lang: {
                  locale: "es_ES",
                  dayFormat: moment.updateLocale("es_ES", {
                    weekdaysMin: ["Dom.", "Lun.", "Mar.", "Mié.", "Jue.", "Vie.", "Sáb."],
                  }),
                },
              }}
              className="enhanced-calendar"
            />
          </div>
        </Spin>
      </div>

      <VerificationModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        selectedItemId={selectedItemId}
        selectedItemType={selectedItemType}
        selectedVerification={selectedVerification}
        setSelectedVerification={setSelectedVerification}
        selectedVisitas={selectedVisitas}
        setSelectedVisitas={setSelectedVisitas}
        loadingModal={loadingModal}
        generalFields={generalFields}
        clienteFields={clienteFields}
        instalacionFields={instalacionFields}
        visitaFields={visitaFields}
      />

      <DayEventsModal
        isDayModalVisible={isDayModalVisible}
        setIsDayModalVisible={setIsDayModalVisible}
        selectedDay={selectedDay}
        dayEvents={getDayEvents()}
        onEventClick={(id, type) => {
          setSelectedItemId(id);
          setSelectedItemType(type);
          setIsDayModalVisible(false);
          setIsModalVisible(true);
        }}
      />
    </ConfigProvider>
  );
};

export default CalendarView;
